import React, { useState, useEffect, useRef } from 'react'
import { SEO } from '../components/shared/seo'
import styled, { createGlobalStyle } from 'styled-components'
import KajabiPhaseFormEmbed from '../components/subscribe/components/KajabiPhaseFormEmbed'

// ─── Data ────────────────────────────────────────────────────────────────────

const FORM_IDS = {
  1: '2149567318',
  2: '2149567322',
  3: '2149567323',
  4: '2149567331',
  5: '2149567332',
}

const PHASE_NAMES = {
  1: 'Phase 1 — Code-First',
  2: 'Phase 2 — Best Practice-First',
  3: 'Phase 3 — Pattern-First',
  4: 'Phase 4 — Responsibility-First',
  5: 'Phase 5 — Value-First',
}

const QUESTIONS = [
  {
    text: "When you start working on a new feature, what's your instinct?",
    context: "Think about what actually happens in your head before you open the editor.",
    opts: [
      { t: "I open the editor and figure it out as I go — tools, APIs, whatever I need to connect", s: 1 },
      { t: "I think about the best practices first — TDD, clean architecture, how to keep it testable from day one", s: 2 },
      { t: "I model the domain first — the ubiquitous language, aggregates, bounded contexts", s: 3 },
      { t: "I think about responsibilities — what this system needs to do and know, and how the roles collaborate", s: 4 },
      { t: "I ask: what's the most valuable path here? Sometimes that's code, sometimes it's a spreadsheet", s: 5 },
    ],
  },
  {
    text: "Something feels wrong with the code. What happens next?",
    context: "You sense something is off. How far can you take it?",
    opts: [
      { t: "I can feel it's wrong but I genuinely can't explain why or what to change", s: 1 },
      { t: "I can name a best practice being violated — like coupling to infrastructure or missing abstraction", s: 2 },
      { t: "I can identify which DDD pattern is missing or being misused and explain the consequences", s: 3 },
      { t: "I can trace it to a specific missing responsibility and design the fix from first principles", s: 4 },
      { t: "I see the system-level implication — I know where this decision leads in 6 months", s: 5 },
    ],
  },
  {
    text: "How would you describe your relationship with testing right now?",
    context: "Not what you know about testing — what you actually do.",
    opts: [
      { t: "I write E2E tests when I have to. They're often flaky and I'm not sure they're valuable", s: 1 },
      { t: "I'm learning to decouple my code from infrastructure so my tests are faster and more stable", s: 2 },
      { t: "I write High Value Unit Tests — testing the application core without touching infrastructure", s: 3 },
      { t: "I design for testability first. The architecture is what makes the testing strategy possible", s: 4 },
      { t: "Testing is a design tool for me. I decide the strategy based on what produces the most value", s: 5 },
    ],
  },
  {
    text: "Why does code get harder to change over time?",
    context: "The real answer isn't 'technical debt'. What's the actual mechanism?",
    opts: [
      { t: "Honestly, I'm not sure — it just happens and I don't know how to prevent it", s: 1 },
      { t: "Missing best practices — no proper abstraction, things are too coupled to infrastructure", s: 2 },
      { t: "You're not using vertical slicing and horizontal decoupling — so concerns bleed into each other", s: 3 },
      { t: "Misaligned responsibilities — when roles take on things they shouldn't, the design rots", s: 4 },
      { t: "Every decision couples the system to itself. Value-first design minimizes irreversible coupling", s: 5 },
    ],
  },
  {
    text: "What's your relationship with Domain-Driven Design?",
    context: "DDD as disintegration and reintegration — breaking a problem apart before rebuilding it intentionally.",
    opts: [
      { t: "I've heard of it but it feels abstract and I don't know where to start", s: 1 },
      { t: "I understand the basics — entities, value objects, repositories — and I'm starting to apply them", s: 2 },
      { t: "I use DDD actively — event storming, ubiquitous language, bounded contexts, CQRS", s: 3 },
      { t: "DDD is one lens I use — I know when it's the right tool and when simpler models are better", s: 4 },
      { t: "I apply the disintegration/reintegration principle everywhere — not just in code", s: 5 },
    ],
  },
  {
    text: "You're in a technical design discussion with senior engineers. What's your experience?",
    context: "Where are you when your position gets challenged?",
    opts: [
      { t: "I mostly listen. I don't feel confident defending positions under pressure", s: 1 },
      { t: "I can contribute, but I sometimes struggle to articulate the why behind my decisions", s: 2 },
      { t: "I can defend my positions and back them up with principles — DDD, patterns, testing strategy", s: 3 },
      { t: "I often lead the framing. I think in tradeoffs, consequences, and design tensions", s: 4 },
      { t: "I shape the decisions. My judgment carries weight. I'm the one people want in the room.", s: 5 },
    ],
  },
  {
    text: "How do you relate to AI coding tools like Copilot or Claude?",
    context: "The question isn't whether you use them — it's who's in charge.",
    opts: [
      { t: "I rely on them heavily — they help me figure out code I wouldn't know how to write myself", s: 1 },
      { t: "I use them as productivity tools but I verify everything — I know enough to catch mistakes", s: 2 },
      { t: "I use them to accelerate implementation. I've already reasoned through the design before they touch it", s: 3 },
      { t: "They handle execution. I handle architecture, design, and the decisions that actually matter", s: 4 },
      { t: "AI amplifies my judgment. The more principled I am, the more powerful the tool becomes", s: 5 },
    ],
  },
]

const PHASES = [
  {
    num: 1,
    name: 'Code-First',
    paradigm: 'Making things work',
    range: [7, 14],
    identity: "You've learned to code — but you haven't yet learned to craft. The fact that you sense something is missing is the first sign you're ready.",
    desc: [
      "At the Code-First phase, you see tools, libraries, and frameworks as the building blocks of software. You think in terms of technology first. You can build things that work — but the code tends to get harder to change over time, testing feels like a chore, and you can feel that something is wrong even when you can't explain it yet.",
      "That feeling is important. Most developers stay here because they don't have a map. You're about to get one.",
    ],
    struggles: [
      "Unknown unknowns — you don't know what you need to know, or how to begin to know it",
      "Tutorial hell and breadth-first learning that keeps you at the surface",
      "E2E tests that are flaky and slow, with no clear alternative",
      "Code getting worse instead of better over time — and not knowing why",
      "No clear path to the next level",
    ],
    next: "Focus on becoming full-stack. Learn layered architectures, write your first E2E tests (they'll suck — that's fine), and start asking: what's the architecture actually doing here?",
    cta: {
      label: 'Your recommended path',
      h: 'Start with solidbook',
      b: "solidbook maps the full landscape of software design, architecture & testing — the 40 years of wisdom nobody handed you. It's the map that makes everything else make sense.",
      p: { t: 'Get solidbook — $29.99', u: 'https://solidbook.io' },
      s: { t: 'Browse the course', u: 'https://essentialist.dev' },
    },
  },
  {
    num: 2,
    name: 'Best Practice-First',
    paradigm: 'Prioritizing quality through principles',
    range: [15, 21],
    identity: "You know something is missing from the way most developers work. You've started finding it — now you need to go deeper than the faux best practices.",
    desc: [
      "You've moved past just making things work. You're studying SOLID, clean architecture, TDD, BDD. You're asking better questions: not just 'how do I make this work' but 'how do I ensure this works well and is valuable?'",
      "The trap at this phase is dogmatism — following common advice without fully understanding the first principles behind it. The shift from Code-First to Best Practice-First is real, but there's a deeper level waiting.",
    ],
    struggles: [
      "Following best practices without fully understanding the first principles behind them",
      "Tests that are better — but still too coupled to infrastructure to be truly fast and stable",
      "Knowing about hexagonal architecture but not yet sure why it matters in practice",
      "Design confidence in familiar contexts — uncertainty everywhere else",
      '"Am I doing this right?" is still the dominant question',
    ],
    next: "Go deep on TDD, BDD, and the Walking Skeleton. Learn why the hexagonal architecture exists — it gives you more testing options, not just cleaner code. Focus on High Value Unit Testing.",
    cta: {
      label: 'Your recommended path',
      h: 'Testing Mastery',
      b: "Testing Mastery takes you through the first two Phases of Craftship in depth — the fastest path from Code-First to Best Practice-First. Then the TSE Mentorship gives you the weekly feedback loop to keep moving.",
      p: { t: 'Enroll in Testing Mastery', u: 'https://www.essentialist.dev/testing-mastery' },
      s: { t: 'Apply for mentorship', u: 'https://tally.so/r/mDqK55' },
    },
  },
  {
    num: 3,
    name: 'Pattern-First',
    paradigm: 'Domain understanding & creative problem solving',
    range: [22, 28],
    identity: "You're becoming a creative problem solver. You can disintegrate a problem, sit with it, understand it — and then reintegrate it intentionally.",
    desc: [
      "This is where DDD comes alive. You're learning that Domain-Driven Design isn't just about aggregates and value objects — it's a method for disintegration and reintegration. Breaking the problem apart, understanding the flows and the language, and then rebuilding it in a way that actually fits the domain.",
      "The dominant question shifts from 'what are the best practices?' to 'what's the DDD-way to solve this?' — which means you're still thinking in patterns, not yet in responsibilities. When faced with a problem the pattern doesn't cover, you can still get stuck.",
    ],
    struggles: [
      "Thinking in patterns — stuck when a pattern doesn't exist or doesn't obviously fit",
      "Strategic design across bounded contexts still feels uncertain",
      "Testing strategy and design confidence in unique or unfamiliar circumstances",
      '"Am I doing this the right DDD-way?" — the dogma shifted, but it\'s still there',
      "The step from pattern-thinking to responsibility-thinking hasn't happened yet",
    ],
    next: "Master High Value Unit Testing on both sides of the stack. Learn CQRS and event-driven architecture. Start asking: what's the responsibility of this object? — not just: which pattern fits here?",
    cta: {
      label: 'Your recommended path',
      h: 'TSE Mentorship — Abstractionist Mastermind',
      b: "At Pattern-First, what you need is a weekly feedback loop with someone who can see exactly where you are and tell you the next right thing to do. The mentorship is built for exactly this.",
      p: { t: 'Apply for the mentorship', u: 'https://tally.so/r/mDqK55' },
      s: { t: 'Join the Discord', u: 'https://discord.gg/MyhQXMvZCr' },
    },
  },
  {
    num: 4,
    name: 'Responsibility-First',
    paradigm: 'Stereotypical roles, responsibilities & collaborations',
    range: [29, 31],
    identity: "You see between the lines. You think in microcosms and macrocosms. You notice design tensions before they become problems.",
    desc: [
      "At the Responsibility-First phase, everything starts to fit together. You no longer just think in patterns — you think in responsibilities, doings, knowings, and the collaborations between roles. You understand the Stereotypical Architecture. You can save companies hundreds of thousands of dollars through better judgment alone.",
      "The challenge now is over-engineering and keeping solutions understandable to the rest of the team.",
    ],
    struggles: [
      "Tendency to over-engineer — not everything needs the full Responsibility-Driven Design treatment",
      "Communicating deep design judgment in a way other developers can understand",
      "Messaging, queues, and distributed system patterns still have unexplored territory",
      "Translating design authority into team-wide influence",
      "Knowing when simpler is better",
    ],
    next: "Learn Responsibility-Driven Design and the Stereotypical Architecture formally. Practice the 3-Step RDD process. Work on Messaging and distributed patterns. Focus on team leadership.",
    cta: {
      label: 'Your recommended path',
      h: 'TSE Mentorship',
      b: "At Responsibility-First, the technical map is largely complete. The work now is integration — holding the identity under pressure, leading teams, and potentially building your own product. Let's talk.",
      p: { t: 'Apply for the mentorship', u: 'https://tally.so/r/mDqK55' },
      s: null,
    },
  },
  {
    num: 5,
    name: 'Value-First',
    paradigm: 'Optimizing the value of everything',
    range: [32, 35],
    identity: "You've completed the technical arc. You have judgment. The question now is: what do you want to build with it?",
    desc: [
      "Value-First developers are fluid. Sometimes they write no tests. Sometimes they write four types. The decision is always the same: what produces the most value in this context?",
      "You're no longer bogged down by programming tasks — that's just breathing. The existential challenge at this phase isn't technical: it's knowing what you want, which problems you want to solve, and having the confidence to take the first step in a new direction.",
    ],
    struggles: [
      "Clarity on what you actually want next — leadership, product, consulting, something else",
      "The confidence to take the leap in a new direction with your full capability behind you",
      "Teaching and transmitting this level of judgment to others",
      "Holding the identity under pressure — the technical knowledge is there, the identity needs integration",
    ],
    next: "Get clear on what you want. Build. Lead. Teach. Apply the disintegration/reintegration principle to your career, not just your code.",
    cta: {
      label: "You're ready for a different conversation",
      h: 'Book a call with Khalil',
      b: "At Value-First, the conversation isn't about the 12 Essentials — it's about what comes next. Career direction, building a product, integrating the identity. Let's figure it out.",
      p: { t: 'Book a call', u: 'https://calendly.com/khalilstemmler/1-on-1-chat' },
      s: null,
    },
  },
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getPhase(total) {
  return PHASES.find(p => total >= p.range[0] && total <= p.range[1]) || PHASES[4]
}

// ─── Styled Components ───────────────────────────────────────────────────────

const GlobalQuizStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Fraunces:ital,wght@0,400;0,600;1,400&display=swap');
`

const Wrap = styled.div`
  font-family: 'JetBrains Mono', monospace;
  max-width: 640px;
  margin: 0 auto;
  padding: 3rem 1.5rem 5rem;
`

const Eyebrow = styled.p`
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #888;
  margin-bottom: 0.75rem;
`

const QuizTitle = styled.h1`
  font-family: 'TriplettCGBlack';
  font-size: 28px;
  font-weight: 600;
  line-height: 1.2;
  margin: 0 0 0.75rem;
`

const QuizSub = styled.p`
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 2.5rem;
`

const ProgressLabel = styled.p`
  font-size: 11px;
  color: #999;
  margin-bottom: 6px;
  letter-spacing: 0.08em;
`

const ProgressBar = styled.div`
  height: 2px;
  background: #e5e5e5;
  margin-bottom: 2rem;
  border-radius: 2px;
`

const ProgressFill = styled.div`
  height: 2px;
  background: #1a1a1a;
  border-radius: 2px;
  transition: width 0.4s ease;
  width: ${p => p.pct}%;
`

const QNum = styled.p`
  font-size: 11px;
  color: #999;
  letter-spacing: 0.1em;
  margin-bottom: 0.75rem;
`

const QText = styled.p`
  font-family: 'Fraunces', serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.45;
  margin: 0 0 0.5rem;
`

const QContext = styled.p`
  font-size: 12px;
  color: #999;
  font-style: italic;
  margin: 0 0 1.5rem;
`

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Option = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  border: ${p => p.selected ? '1px solid #1a1a1a' : '0.5px solid #ddd'};
  border-radius: 8px;
  cursor: pointer;
  background: ${p => p.selected ? '#f5f5f5' : '#fff'};
  transition: all 0.15s ease;

  &:hover {
    border-color: #999;
    background: #f9f9f9;
  }
`

const OptionKey = styled.span`
  font-size: 11px;
  color: ${p => p.selected ? '#1a1a1a' : '#aaa'};
  font-weight: ${p => p.selected ? '500' : '400'};
  min-width: 18px;
  margin-top: 2px;
  letter-spacing: 0.05em;
  flex-shrink: 0;
`

const OptionText = styled.span`
  font-size: 13px;
  color: ${p => p.selected ? '#1a1a1a' : '#555'};
  line-height: 1.5;
`

const NavRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
`

const Btn = styled.button`
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.06em;
  padding: 10px 20px;
  border: 0.5px solid #ccc;
  border-radius: 8px;
  cursor: pointer;
  background: ${p => p.primary ? '#1a1a1a' : 'transparent'};
  color: ${p => p.primary ? '#fff' : '#1a1a1a'};
  transition: all 0.15s ease;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`

const Divider = styled.hr`
  border: none;
  border-top: 0.5px solid #eee;
  margin: 1.5rem 0;
`

const PhaseLabel = styled.p`
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #999;
  margin-bottom: 0.5rem;
`

const PhaseName = styled.h2`
  font-family: 'Fraunces', serif;
  font-size: 32px;
  font-weight: 600;
  margin: 0 0 1rem;
`

const ParadigmTag = styled.span`
  display: inline-block;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 3px 8px;
  border: 0.5px solid #ddd;
  border-radius: 6px;
  color: #999;
  margin-bottom: 1rem;
`

const Identity = styled.p`
  font-size: 13px;
  color: #777;
  font-style: italic;
  border-left: 2px solid #eee;
  padding-left: 1rem;
  line-height: 1.6;
  margin-bottom: 1.75rem;
`

const Desc = styled.p`
  font-size: 14px;
  color: #555;
  line-height: 1.75;
  margin-bottom: 1rem;
`

const SectionTitle = styled.p`
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #999;
  margin-bottom: 0.75rem;
`

const ResultList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.75rem;

  li {
    font-size: 13px;
    color: #555;
    padding: 7px 0;
    border-bottom: 0.5px solid #eee;
    display: flex;
    gap: 10px;
    line-height: 1.5;

    &:last-child { border-bottom: none; }
    &::before { content: '→'; color: #bbb; flex-shrink: 0; }
  }
`

const CtaBox = styled.div`
  border: 0.5px solid #eee;
  border-radius: 12px;
  padding: 1.25rem;
  background: #fafafa;
`

const CtaLabel = styled.p`
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #999;
  margin-bottom: 0.5rem;
`

const CtaHeadline = styled.p`
  font-family: 'Fraunces', serif;
  font-size: 18px;
  margin: 0 0 0.5rem;
`

const CtaBody = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0 0 1rem;
  line-height: 1.6;
`

const CtaActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`

const Pips = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 2rem;
`

const Pip = styled.div`
  height: 3px;
  flex: 1;
  border-radius: 2px;
  background: ${p => p.active ? '#1a1a1a' : '#eee'};
`

const GateTitle = styled.h2`
  font-family: 'Fraunces', serif;
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 0.75rem;
  line-height: 1.3;
`

const GateSub = styled.p`
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 1.5rem;
`

const GatePhaseLabel = styled.span`
  display: inline-block;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 4px 10px;
  border: 0.5px solid #ddd;
  border-radius: 6px;
  color: #999;
  margin-bottom: 1.5rem;
`

const PrivacyNote = styled.p`
  font-size: 11px;
  color: #bbb;
  margin-top: 1rem;
  text-align: center;
`

const KajabiWrap = styled.div`
  min-height: 120px;

  iframe {
    width: 100% !important;
  }
`

// ─── Quiz Step ───────────────────────────────────────────────────────────────

function QuizStep({ current, total, answers, onSelect, onNext, onBack }) {
  const q = QUESTIONS[current]
  const pct = Math.round(((current + 1) / total) * 100)
  const keys = ['A', 'B', 'C', 'D', 'E']

  return (
    <div>
      <ProgressLabel>Question {current + 1} of {total}</ProgressLabel>
      <ProgressBar><ProgressFill pct={pct} /></ProgressBar>

      <div style={{ animation: 'fadeUp 0.3s ease' }}>
        <QNum>Q{current + 1}</QNum>
        <QText>{q.text}</QText>
        {q.context && <QContext>{q.context}</QContext>}
        <Options>
          {q.opts.map((opt, i) => (
            <Option key={i} selected={answers[current] === i} onClick={() => onSelect(i)}>
              <OptionKey selected={answers[current] === i}>{keys[i]}</OptionKey>
              <OptionText selected={answers[current] === i}>{opt.t}</OptionText>
            </Option>
          ))}
        </Options>
      </div>

      <NavRow>
        <Btn onClick={onBack} disabled={current === 0}>← back</Btn>
        <Btn primary onClick={onNext} disabled={answers[current] === null}>
          {current === total - 1 ? 'see my results →' : 'next →'}
        </Btn>
      </NavRow>
    </div>
  )
}

// ─── Gate Step ───────────────────────────────────────────────────────────────

function GateStep({ phaseNum, onSuccess }) {
  useEffect(() => {
    // Listen for Kajabi success event via postMessage
    function onMessage(e) {
      if (
        e.data === 'kajabi:form:success' ||
        (e.data && e.data.type === 'kajabi:form:success')
      ) {
        onSuccess()
      }
    }
    window.addEventListener('message', onMessage)

    // Fallback: hook into form submit directly
    const fallbackTimer = setTimeout(() => {
      const forms = document.querySelectorAll('#kajabi-form form')
      forms.forEach(f => {
        if (!f._hooked) {
          f._hooked = true
          f.addEventListener('submit', () => setTimeout(onSuccess, 1200))
        }
      })
    }, 1000)

    return () => {
      window.removeEventListener('message', onMessage)
      clearTimeout(fallbackTimer)
    }
  }, [onSuccess])

  return (
    <div>
      <Eyebrow>Almost there</Eyebrow>
      <GateTitle>Where should we send your results?</GateTitle>
      <GateSub>
        You'll get your phase, a clear breakdown of where you're at, and a personalized path
        forward — delivered to your inbox.
      </GateSub>
      <GatePhaseLabel>{PHASE_NAMES[phaseNum]}</GatePhaseLabel>
      <KajabiWrap>
        <KajabiPhaseFormEmbed formId={FORM_IDS[phaseNum]} onSuccess={onSuccess} />
      </KajabiWrap>
      <PrivacyNote>No spam. Unsubscribe anytime.</PrivacyNote>
    </div>
  )
}

// ─── Result Step ─────────────────────────────────────────────────────────────

function ResultStep({ phase, onRetake }) {
  return (
    <div>
      <Eyebrow>Your result</Eyebrow>
      <Pips>
        {[1, 2, 3, 4, 5].map(n => <Pip key={n} active={n <= phase.num} />)}
      </Pips>
      <PhaseLabel>Phase {phase.num} of 5</PhaseLabel>
      <PhaseName>{phase.name}</PhaseName>
      <ParadigmTag>paradigm: {phase.paradigm}</ParadigmTag>
      <Identity>"{phase.identity}"</Identity>

      {phase.desc.map((p, i) => <Desc key={i}>{p}</Desc>)}

      <Divider />

      <SectionTitle>Where you're feeling friction</SectionTitle>
      <ResultList>
        {phase.struggles.map((s, i) => <li key={i}>{s}</li>)}
      </ResultList>

      <SectionTitle>What to focus on next</SectionTitle>
      <ResultList>
        <li>{phase.next}</li>
      </ResultList>

      <Divider />

      <CtaBox>
        <CtaLabel>{phase.cta.label}</CtaLabel>
        <CtaHeadline>{phase.cta.h}</CtaHeadline>
        <CtaBody>{phase.cta.b}</CtaBody>
        <CtaActions>
          <a href={phase.cta.p.u} target="_blank" rel="noopener noreferrer">
            <Btn primary>{phase.cta.p.t}</Btn>
          </a>
          {phase.cta.s && (
            <a href={phase.cta.s.u} target="_blank" rel="noopener noreferrer">
              <Btn>{phase.cta.s.t}</Btn>
            </a>
          )}
        </CtaActions>
      </CtaBox>

      <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
        <Btn onClick={onRetake} style={{ fontSize: '11px', color: '#bbb' }}>
          retake the quiz
        </Btn>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const STEPS = { QUIZ: 'quiz', GATE: 'gate', RESULT: 'result' }

export default function PhasesQuizPage() {
  const [step, setStep] = useState(STEPS.QUIZ)
  const [current, setCurrent] = useState(0)
  const [answers, setAnswers] = useState(new Array(QUESTIONS.length).fill(null))
  const [resultPhase, setResultPhase] = useState(null)

  function handleSelect(optionIndex) {
    const next = [...answers]
    next[current] = optionIndex
    setAnswers(next)
  }

  function handleNext() {
    if (answers[current] === null) return
    if (current < QUESTIONS.length - 1) {
      setCurrent(current + 1)
    } else {
      // Calculate score and move to gate
      const total = answers.reduce((sum, a, i) => sum + (a !== null ? QUESTIONS[i].opts[a].s : 0), 0)
      setResultPhase(getPhase(total))
      setStep(STEPS.GATE)
    }
  }

  function handleBack() {
    if (current > 0) setCurrent(current - 1)
  }

  function handleGateSuccess() {
    setStep(STEPS.RESULT)
  }

  function handleRetake() {
    setStep(STEPS.QUIZ)
    setCurrent(0)
    setAnswers(new Array(QUESTIONS.length).fill(null))
    setResultPhase(null)
  }

  return (
    <>
      <SEO
        title="Which Phase of Craftship are you in?"
        keywords={['software design', 'software architecture', 'phases of craftship', 'developer quiz', 'khalil stemmler']}
        description="7 questions to identify your mental operating system as a developer — and get a clear, personalized path to the next level."
      />
      <GlobalQuizStyles />
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <Wrap>
        {step === STEPS.QUIZ && (
          <>
            {current === 0 && (
              <div style={{ marginBottom: '2.5rem' }}>
                <Eyebrow>The Software Essentialist</Eyebrow>
                <QuizTitle>Which Phase of Craftship are you in?</QuizTitle>
                <QuizSub>
                  7 questions. Identify your mental operating system as a developer — and get a
                  clear path to the next level.
                </QuizSub>
              </div>
            )}
            <QuizStep
              current={current}
              total={QUESTIONS.length}
              answers={answers}
              onSelect={handleSelect}
              onNext={handleNext}
              onBack={handleBack}
            />
          </>
        )}

        {step === STEPS.GATE && resultPhase && (
          <GateStep phaseNum={resultPhase.num} onSuccess={handleGateSuccess} />
        )}

        {step === STEPS.RESULT && resultPhase && (
          <ResultStep phase={resultPhase} onRetake={handleRetake} />
        )}
      </Wrap>
    </>
  )
}